import apiClient, { type ApiError, setAuthToken } from './apiClient'
import type { ExtendedAxiosRequestConfig } from './types'
import type {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  ChangeRoleRequest,
  ChangeRoleResponse,
} from '@/types/auth'
import { clearAuthStorage, clearImpersonationStorage, getAuthTokenFromStorage } from '@/lib/utils/storage'
import { beginIntentionalSessionEnd } from '@/lib/auth/sessionActivity'
import { extractErrorMessage } from '@/lib/utils/errorHandling'
import { API_ENDPOINTS } from './config'

/**
 * Login user
 * @param credentials - Email and password
 * @returns Login response with token and user data
 * @throws ApiError if login fails
 */
export async function login(credentials: LoginRequest): Promise<LoginResponse> {
  try {
    const response = await apiClient.post<LoginResponse | string>(API_ENDPOINTS.LOGIN, credentials, {
      skipAuth: true,
    } as ExtendedAxiosRequestConfig)

    const data = response.data

    // API might return plain string for errors
    if (typeof data === 'string') {
      throw {
        message: data,
        status: 401,
      } as ApiError
    }

    // Validate response - API returns token directly without success property
    if (!data.token) {
      throw {
        message: data.message || 'Login failed. Please check your credentials and try again.',
        status: 401,
      } as ApiError
    }

    // Add success property for consistency
    data.success = true

    // Set auth token for subsequent requests
    setAuthToken(data.token)

    // Angular parity: on login success clear impersonation so applicant doesn't see "Stop Impersonate" after re-login
    clearImpersonationStorage()

    return data
  } catch (error: unknown) {
    // Handle axios error with response data
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { data?: unknown; status?: number } }
      const responseData = axiosError.response?.data

      // API returns plain string errors
      if (typeof responseData === 'string') {
        throw {
          message: responseData,
          status: axiosError.response?.status || 401,
        } as ApiError
      }
    }

    const apiError = error as ApiError

    // Extract error message
    const errorMessage = extractErrorMessage(error, 'Login failed. Please check your credentials and try again.')

    throw {
      message: errorMessage,
      status: apiError.status || 401,
    } as ApiError
  }
}

/**
 * Register new user
 * @param userData - Registration data
 * @returns Registration response
 * @throws ApiError if registration fails
 */
export async function register(userData: RegisterRequest): Promise<RegisterResponse> {
  try {
    const response = await apiClient.post<RegisterResponse | string>(API_ENDPOINTS.REGISTER, userData, {
      skipAuth: true,
    } as ExtendedAxiosRequestConfig)

    const data = response.data

    // API returns plain string for both success and errors
    if (typeof data === 'string') {
      const lowerData = data.toLowerCase()

      // Check if it's a success message first
      if (
        lowerData.includes('success') ||
        lowerData.includes('registered') ||
        lowerData.includes('created') ||
        lowerData.includes('welcome')
      ) {
        // Success message as string
        return {
          success: true,
          message: data,
        }
      }

      // Check if it's an error message
      if (
        lowerData.includes('error') ||
        lowerData.includes('already') ||
        lowerData.includes('invalid') ||
        lowerData.includes('failed') ||
        lowerData.includes('exists') ||
        lowerData.includes('in use') ||
        lowerData.includes('not found')
      ) {
        throw {
          message: data,
          status: 400,
        } as ApiError
      }

      // Default: treat as success if HTTP 200
      return {
        success: true,
        message: data,
      }
    }

    // Check for success property in object response
    if (data && typeof data === 'object') {
      if ('success' in data && !data.success) {
        throw {
          message: (data as RegisterResponse).message || 'Registration failed. Please try again.',
          status: 400,
        } as ApiError
      }
      return data as RegisterResponse
    }

    // Default success
    return {
      success: true,
      message: 'Registration successful',
    }
  } catch (error: unknown) {
    // Handle axios error with response data
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { data?: unknown; status?: number } }
      const responseData = axiosError.response?.data

      // API returns plain string errors
      if (typeof responseData === 'string') {
        throw {
          message: responseData,
          status: axiosError.response?.status || 400,
        } as ApiError
      }
    }

    const apiError = error as ApiError

    // Extract error message
    const errorMessage = extractErrorMessage(error, 'Registration failed. Please try again.')

    throw {
      message: errorMessage,
      status: apiError.status || 400,
      errors: apiError.errors,
    } as ApiError
  }
}

/**
 * Register and auto-login
 * @param userData - Registration data
 * @returns Login response after auto-login
 * @throws ApiError if registration or login fails
 */
export async function registerAndLogin(userData: RegisterRequest): Promise<LoginResponse> {
  // First register the user
  await register(userData)

  // Then auto-login
  const loginResponse = await login({
    email: userData.email,
    password: userData.password,
  })

  return loginResponse
}

/**
 * Logout API response
 */
export interface LogoutResponse {
  success: boolean
  message: string
}

/**
 * Logout user
 * 1. Calls the backend logout endpoint (while the auth token is still in storage
 *    so the request interceptor can attach it to the request).
 * 2. Waits for the backend to invalidate the session (max 5 seconds).
 * 3. Clears local auth state AFTER the backend call completes (or times out).
 *
 * This order is critical: the token MUST be in localStorage when the request
 * is created so the interceptor can attach it. Clearing storage first would
 * send an unauthenticated request that the backend ignores.
 *
 * @returns Logout response (always succeeds locally)
 */
export async function logout(): Promise<LogoutResponse> {
  try {
    // Start the backend logout call WHILE the token is still in localStorage.
    // The request interceptor reads the token synchronously when this line executes
    // and attaches it to the outgoing request headers.
    const logoutRequest = apiClient.get(API_ENDPOINTS.LOGOUT)

    // Wait for the backend to process the logout, but cap at 5 seconds
    // so the user is never stuck if the backend is unresponsive.
    await Promise.race([
      logoutRequest.catch(() => {
        // Swallow errors — session may already be expired (401), which is fine
      }),
      new Promise(resolve => setTimeout(resolve, 5000)),
    ])
  } catch {
    // Unexpected error — proceed with local cleanup regardless
  }

  // NOW clear local state — backend has had its chance to invalidate the session
  setAuthToken(null)
  clearAuthStorage()

  return {
    success: true,
    message: 'Logged out successfully',
  }
}

/**
 * URL to redirect to after logout.
 * Used when we need to logout, open another URL in a new tab, then redirect the current tab.
 */
export function getLogoutRedirectUrl(): string {
  return '/login'
}

/**
 * Logout and redirect to the login page in one atomic operation.
 *
 * This MUST be used instead of dispatching `logoutUser()` + manual redirect,
 * because the thunk changes Redux `isAuthenticated`, which races the redirect.
 * Calling `logout()` directly (no Redux dispatch) and redirecting immediately
 * avoids that race entirely.
 */
export async function logoutAndRedirect(): Promise<void> {
  beginIntentionalSessionEnd()
  await logout()
  window.location.href = getLogoutRedirectUrl()
}

/**
 * Change user role
 * @param request - Change role request with role_id and optional OTP
 * @returns Change role response
 * @throws ApiError if change role fails
 */
export async function changeRole(request: ChangeRoleRequest): Promise<ChangeRoleResponse> {
  try {
    const response = await apiClient.post<ChangeRoleResponse>(API_ENDPOINTS.CHANGE_ROLE, request)

    const data = response.data

    // Validate response
    if (!data || typeof data !== 'object') {
      throw {
        message: 'Invalid response from server',
        status: 500,
      } as ApiError
    }

    return data
  } catch (error: unknown) {
    // Handle axios error with response data
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { data?: unknown; status?: number } }
      const responseData = axiosError.response?.data

      // API returns plain string errors
      if (typeof responseData === 'string') {
        throw {
          message: responseData,
          status: axiosError.response?.status || 400,
        } as ApiError
      }
    }

    const apiError = error as ApiError

    // Extract error message
    const errorMessage = extractErrorMessage(error, 'Failed to change role. Please try again.')

    throw {
      message: errorMessage,
      status: apiError.status || 400,
    } as ApiError
  }
}

/**
 * Check if user is authenticated
 * @returns True if user has valid token
 */
export function isAuthenticated(): boolean {
  return !!getAuthTokenFromStorage()
}

